import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";

import { useCustomerIdGuard } from "../../../../common/hooks/useCustomerIdGuard";
import StyledLoadingButton from "../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import {
  errorNotification,
  successNotification,
} from "../../../../common/variables/notification";
import client from "../../../../configs/apolloClient";
import LocationForm from "./components/LocationForm";
import { useGetLocationById } from "./hooks/useGetLocationById";
import { useUpdateLocation } from "./hooks/useUpdateLocation";
import BreadcrumbNavigation from "../../../../common/components/tabs/BreadcrumbNavigation";
import { RouteEnum } from "../../../../common/models/enums";
import PaperWrapperCentered from "../../../../common/components/item/PaperWrapperCentered";
import { ILocationForm } from "./variables/locationData";
import { TagInput, TagOfAssociation } from "../../../../API";
import CircularLoading from "../../../../common/components/progress/CircularLoading";
import { useForm } from "react-hook-form";

const EditLocationPage = (): JSX.Element => {
  const navigate = useNavigate();
  const selectedCustomerId = useCustomerIdGuard();
  const routerLocation = useLocation();

  const { data, loading: dataLoading } = useGetLocationById();
  const { updateLocation, loading } = useUpdateLocation();

  const existingLocation = data?.getLocationById;

  const defaultValues: Partial<ILocationForm> = useMemo(() => {
    if (!existingLocation) return {};

    const parsedCoords = existingLocation.locationData
      ? JSON.parse(existingLocation.locationData)
      : {};

    return {
      rowId: existingLocation.id,
      locationName: existingLocation.name ?? "",
      referenceId: existingLocation.referenceId ?? "",
      latitude: parsedCoords.lat ?? "",
      longitude: parsedCoords.lon ?? "",
      timeZone: existingLocation.timeZone ?? "",
      tags: existingLocation.tags ?? [],
    };
  }, [existingLocation]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ILocationForm>({
    defaultValues,
  });

  useEffect(() => {
    if (!data?.getLocationById) return;

    let parsedCoords = { lat: "", lon: "" };

    try {
      parsedCoords = JSON.parse(data.getLocationById.locationData ?? "{}");
    } catch (e) {
      parsedCoords = { lat: "", lon: "" };

      console.error("Failed to parse locationData:", e);
    }

    setValue("rowId", data.getLocationById.id);

    setValue("locationName", data.getLocationById.name ?? "");

    setValue("referenceId", data.getLocationById.referenceId ?? "");

    setValue("latitude", parsedCoords.lat);

    setValue("longitude", parsedCoords.lon);

    setValue("timeZone", data.getLocationById.timeZone ?? "");

    setValue("tags", data.getLocationById.tags ?? []);
  }, [data?.getLocationById]);

  const handleCancel = (): void => {
    navigate(-1);
  };

  const onSubmit = async (form: ILocationForm): Promise<void> => {
    const key = form.locationName.toLowerCase().replace(/\s+/g, "_");
    const locationId = `${selectedCustomerId}#L#${key}`;

    const normalizedId =
      client.cache.identify({
        id: locationId,
        __typename: "Location",
      }) ?? "";

    const extract = client.cache.extract();

    if (
      normalizedId &&
      extract[normalizedId]?.name === form.locationName &&
      form.locationName !== routerLocation.state?.existingRowName
    ) {
      errorNotification("Location name already exists");

      return;
    }

    let tagsToDelete: Omit<TagOfAssociation, "__typename">[] = [];
    let tagsToCreate: TagInput[] = [];

    const tagsOfLocation = existingLocation?.tags ?? [];

    tagsToDelete = tagsOfLocation
      .filter(tag => !form.tags.some(locationTag => locationTag.id === tag.id))
      .map(tag => ({
        id: tag.id,
        tagName: tag.tagName,
        associationId: tag.associationId,
      }));

    tagsToCreate = form.tags
      .filter(
        tag => !tagsOfLocation.some(locationTag => locationTag.id === tag.id)
      )
      .map(tag => ({
        id: tag.id,
        tagName: tag.tagName,
      }));

    try {
      await updateLocation({
        customerId: selectedCustomerId,
        locationId: form.rowId,
        name: form.locationName,
        referenceId: form.referenceId,
        locationData: JSON.stringify({
          lat: form.latitude,
          lon: form.longitude,
        }),
        tags: tagsToCreate,
        tagsToDelete,
        timeZone: form.timeZone,
      });

      successNotification(
        `Location "${form.locationName}" updated successfully`
      );

      navigate(-1);
    } catch (err) {
      console.error(err);

      errorNotification("Failed to update location");
    }
  };

  const locationPath = routerLocation.pathname.includes("sensory")
    ? RouteEnum.SensorySystemSettings
    : RouteEnum.CustomerSettings;

  const breadcrumbItems = [
    { label: "Customer settings", path: locationPath },
    { label: "Edit location" },
  ];

  return (
    <>
      <Box sx={{ marginBottom: "1em" }}>
        <BreadcrumbNavigation items={breadcrumbItems} />
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h3" sx={{ paddingBottom: "1em" }}>
          Edit location
        </Typography>

        <PaperWrapperCentered sx={{ minHeight: "300px" }}>
          {!dataLoading && existingLocation ? (
            <LocationForm control={control} errors={errors} />
          ) : (
            <CircularLoading />
          )}
        </PaperWrapperCentered>

        <Stack direction="row-reverse" spacing={2} sx={{ paddingTop: 3 }}>
          <StyledLoadingButton
            type="submit"
            form="location-form"
            sx={{ marginTop: "1.5em" }}
            loading={loading}
            loadingPosition="start"
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
          >
            Save changes
          </StyledLoadingButton>

          <StyledLoadingButton
            sx={{ marginTop: "1.5em" }}
            loading={loading}
            loadingPosition="start"
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
          >
            Cancel
          </StyledLoadingButton>
        </Stack>
      </form>
    </>
  );
};

export default EditLocationPage;
